import { useState } from "react";
import { api } from "./api/client";
import { useAsync } from "./hooks/useAsync";
import { useMigration } from "./hooks/useMigration";
import { AccountGrid } from "./components/AccountGrid";
import { BotDetail } from "./components/BotDetail";
import { BotGrid, BotGridSkeleton } from "./components/BotGrid";
import { DOCS_HOME, Guide, isDocsPath } from "./components/Guide";
import { routedClick, useRoute } from "./router";
import { HealthBar } from "./components/HealthBar";
import { ResultPanel } from "./components/ResultPanel";
import { StageList } from "./components/StageList";
import { ACCOUNTS, type Account } from "./accounts";
import type { LexBot, MigrationRequest, Planner } from "./types";

type Screen = "account" | "pick" | "convert" | "result";

/**
 * What every migration does.
 *
 * Not offered as choices any more: a migration that stops at "created" leaves
 * drafts nobody can call, so building, deploying and wiring up the contact
 * flow are what "convert" means rather than options on top of it.
 */
const FIXED: Omit<MigrationRequest, "bot"> = {
  deploy: true,
  contactFlow: true,
  environment: "production",
};

const PLANNER: Planner = "rules";

export default function App() {
  const health = useAsync(() => api.health(), []);

  const [path, navigate] = useRoute();
  const inDocs = isDocsPath(path);

  const [screen, setScreen] = useState<Screen>("account");
  const [account, setAccount] = useState<Account | null>(null);
  const [selected, setSelected] = useState<LexBot | null>(null);

  // Held until an account is chosen: picking one is what loads its bots.
  const bots = useAsync(() => api.bots(), [account?.id], {
    enabled: account !== null,
  });

  /*
   * The chosen bot's intents and slots.
   *
   * Held until the Convert screen, not fetched with the list: it is a full
   * Lex export per bot, and on the picker it would be several of them for
   * the one that gets chosen.
   */
  const detail = useAsync(
    () => api.bot(selected!.botId),
    [selected?.botId],
    { enabled: screen === "convert" && selected !== null },
  );

  const migration = useMigration();
  const busy = migration.running;
  const finished = !!migration.result || !!migration.error;

  function convert() {
    if (!selected) return;
    void migration.run(PLANNER, { bot: selected.botId, ...FIXED });
  }

  function chooseAccount(next: Account) {
    setAccount(next);
    setSelected(null);
    setScreen("pick");
  }

  function startOver() {
    // Drop the finished run as well as the choice. Without this, picking a bot
    // again lands on a Convert screen showing the previous migration's stages,
    // all ticked, for a bot that has not been converted yet.
    migration.reset();
    setSelected(null);
    // Back to the bots, not the accounts: another bot in the same account is
    // the next thing someone wants far more often than another account.
    setScreen("pick");
  }

  function changeAccount() {
    migration.reset();
    setSelected(null);
    setAccount(null);
    setScreen("account");
  }

  // The guide is its own page with its own chrome; the app header would
  // otherwise sit above it and belong to neither.
  if (inDocs) return <Guide path={path} navigate={navigate} />;

  return (
    <>
      <header className="bar">
        <div className="brand">
          <span className="mark">CX</span>
          <div>
            <h1>Lex &rarr; CX Designer</h1>
            <p className="sub">
              Recreate an Amazon Lex V2 bot as an Amazon Connect CX Designer
              application.
            </p>
          </div>
        </div>
        <div className="head-actions">
          <HealthBar health={health.data} error={health.error} />
          <a
            href={DOCS_HOME}
            className="icon-btn"
            onClick={routedClick(navigate, DOCS_HOME)}
            title="How to use this tool"
            aria-label="How to use this tool"
          >
            <svg viewBox="0 0 24 24" width="17" height="17" aria-hidden="true">
              <path
                d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8l-5-5Z M14 3v5h5 M9 13h6 M9 17h6"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </a>
        </div>
      </header>

      <main>
        {screen === "account" && (
          <section className="panel">
            <div className="panel-head">
              <div className="step-title">
                <h2>Choose an account</h2>
              </div>
            </div>

            <AccountGrid
              accounts={ACCOUNTS}
              selectedId={account?.id ?? null}
              onSelect={chooseAccount}
            />
          </section>
        )}

        {screen === "pick" && (
          <section className="panel">
            <div className="panel-head">
              <div className="step-title">
                <h2>Choose a Lex bot</h2>
              </div>
              <div className="head-actions">
              <button
                type="button"
                className="ghost"
                onClick={changeAccount}
              >
                {"←"} {account?.name ?? "Accounts"}
              </button>
              <button
                type="button"
                className={`icon-btn ${bots.loading ? "spinning" : ""}`}
                onClick={bots.reload}
                disabled={bots.loading}
                title="Reload the bot list"
                aria-label="Reload the bot list"
              >
                <svg viewBox="0 0 24 24" width="17" height="17" aria-hidden="true">
                  <path
                    d="M20 11a8 8 0 1 0-.6 3M20 5v6h-6"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              </div>
            </div>

            {bots.error && <p className="error">{bots.error}</p>}

            {/*
              Skeleton or grid, never both: `data` survives a reload, so
              rendering on `data` alone would leave the stale list up beside
              the placeholders while the new one is fetched.
            */}
            {bots.loading ? (
              <>
                <p className="sr-only" role="status">
                  Loading Lex bots
                </p>
                <BotGridSkeleton />
              </>
            ) : (
              bots.data && (
                <BotGrid
                  bots={bots.data}
                  selectedId={selected?.botId ?? null}
                  onSelect={setSelected}
                />
              )
            )}

            <div className="actions-row">
              <button
                type="button"
                className="primary"
                disabled={!selected}
                onClick={() => setScreen("convert")}
              >
                Next &rarr;
              </button>
            </div>
          </section>
        )}

        {screen === "convert" && selected && (
          <section className="panel">
            <div className="panel-head">
              <div className="step-title">
                <h2>Convert</h2>
              </div>
              <button
                type="button"
                className="ghost"
                onClick={startOver}
                // Going back mid-run would orphan a migration that is
                // creating real resources.
                disabled={busy}
              >
                {"\u2190"} Choose another
              </button>
            </div>

            <div className="chosen">
              <span className="pod-icon" aria-hidden="true">
                {selected.name.slice(0, 2).toUpperCase()}
              </span>
              <span className="chosen-body">
                <span className="chosen-name">{selected.name}</span>
                <span className="chosen-meta">
                  <code>{selected.botId}</code>
                  {selected.locales?.map((l) => (
                    <span key={l} className="tag">
                      {l}
                    </span>
                  ))}
                </span>
              </span>
            </div>

            {!busy && !finished && (
              <BotDetail
                detail={detail.data}
                loading={detail.loading}
                error={detail.error}
              />
            )}

            {!finished && (
              <div className="actions-row">
                <button
                  type="button"
                  className="primary"
                  onClick={convert}
                  disabled={busy}
                >
                  {busy ? "Converting\u2026" : "Convert to CX Designer"}
                </button>
                <span className="muted inline">
                  {busy
                    ? "Building, deploying and wiring up the contact flow."
                    : "Creates, builds, deploys and connects the application."}
                </span>
              </div>
            )}

            {(busy || finished) && (
              <div className="progress-block">
                <StageList
                  stages={migration.stages}
                  activity={migration.activity}
                  running={busy}
                />
              </div>
            )}

            {finished && (
              <div className="actions-row">
                <button
                  type="button"
                  className="primary"
                  onClick={() => setScreen("result")}
                >
                  View result &rarr;
                </button>
              </div>
            )}
          </section>
        )}

        {screen === "result" && (
          <section className="panel">
            <div className="panel-head">
              <div className="step-title">
                <h2>Result</h2>
              </div>
            </div>

            <ResultPanel
              running={false}
              botName={selected?.name ?? null}
              planner={PLANNER}
              result={migration.result}
              error={migration.error}
            />

            <div className="actions-row">
              <button type="button" className="primary" onClick={startOver}>
                Migrate another Lex bot
              </button>
            </div>
          </section>
        )}
      </main>
    </>
  );
}
